import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Upload, Link2, Trash2, RefreshCw, FileText, Globe, Plus, Pencil, Check, X, AlertTriangle, CheckCircle2, FolderPlus, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const TIPO_ICON = { pdf: FileText, docx: FileText, txt: FileText, url: Globe };

function StatoBadge({ d }) {
  if (d.stato === "in_lettura") return <Badge variant="secondary" className="gap-1" data-testid={`lib-stato-${d.id}`}><Loader2 className="h-3 w-3 animate-spin" />In lettura…</Badge>;
  if (d.stato === "errore") return <Badge variant="destructive" className="gap-1" data-testid={`lib-stato-${d.id}`}><AlertTriangle className="h-3 w-3" />Errore</Badge>;
  return <Badge variant="outline" className="gap-1 text-emerald-600" data-testid={`lib-stato-${d.id}`}><CheckCircle2 className="h-3 w-3" />Pronto · {d.blocchi + (d.blocchi_visione || 0)} blocchi</Badge>;
}

function CategorieManager({ cats, reload }) {
  const [nuova, setNuova] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const add = async () => {
    if (!nuova.trim()) return;
    try { await api.post("/vesta-libreria/categorie", { nome: nuova.trim() }); setNuova(""); reload(); toast.success("Categoria aggiunta"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const save = async (id) => {
    try { await api.put(`/vesta-libreria/categorie/${id}`, { nome: editVal.trim() }); setEditId(null); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (c) => {
    if (!window.confirm(`Eliminare la categoria «${c.nome}»? I suoi documenti verranno spostati in «Altro».`)) return;
    try { await api.delete(`/vesta-libreria/categorie/${c.id}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="lib-categorie-manager">
      {cats.map((c) => (
        <div key={c.id} className="flex items-center gap-1 rounded-full border px-2 py-1 text-xs" data-testid={`lib-cat-${c.id}`}>
          {editId === c.id ? (
            <>
              <input value={editVal} onChange={(e) => setEditVal(e.target.value)} className="w-32 bg-transparent outline-none" data-testid={`lib-cat-edit-input-${c.id}`} autoFocus />
              <button onClick={() => save(c.id)} data-testid={`lib-cat-save-${c.id}`}><Check className="h-3.5 w-3.5 text-emerald-600" /></button>
              <button onClick={() => setEditId(null)}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>
            </>
          ) : (
            <>
              <span className="font-medium">{c.nome}</span>
              {!c.protetta && <button onClick={() => { setEditId(c.id); setEditVal(c.nome); }} data-testid={`lib-cat-edit-${c.id}`}><Pencil className="h-3 w-3 text-muted-foreground hover:text-foreground" /></button>}
              {!c.protetta && <button onClick={() => del(c)} data-testid={`lib-cat-del-${c.id}`}><Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" /></button>}
            </>
          )}
        </div>
      ))}
      <div className="flex items-center gap-1">
        <Input value={nuova} onChange={(e) => setNuova(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nuova categoria" className="h-8 w-44 text-xs" data-testid="lib-cat-new-input" />
        <Button size="sm" variant="outline" onClick={add} data-testid="lib-cat-add-btn"><FolderPlus className="mr-1 h-4 w-4" />Aggiungi</Button>
      </div>
    </div>
  );
}

function DirettiveEditor({ d, reload }) {
  const [val, setVal] = useState(d.direttive || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(d.direttive || ""); }, [d.direttive]);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/vesta-libreria/${d.id}`, { direttive: val }); toast.success("Direttive salvate: Vesta le userà per interpretare il documento"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setSaving(false);
  };
  const dirty = (val || "") !== (d.direttive || "");
  return (
    <div className="rounded-md border bg-muted/30 p-2" data-testid={`lib-direttive-${d.id}`}>
      <label className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground"><Info className="h-3.5 w-3.5" />Direttive per Vesta: come interpretare questo documento (su cosa puntare, cosa dire, cosa non dire)</label>
      <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={2} placeholder="Es. Documento sugli incentivi 2026. Punta sulla detrazione del 50% e sul risparmio in bolletta. Non citare importi massimi non confermati. Tono commerciale verso l'installatore." className="text-sm" data-testid={`lib-direttive-input-${d.id}`} />
      {dirty && (
        <div className="mt-1 flex justify-end">
          <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid={`lib-direttive-save-${d.id}`}>
            {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}Salva direttive
          </Button>
        </div>
      )}
    </div>
  );
}

export function LibreriaDocumenti() {
  const [data, setData] = useState({ categorie: [], documenti: [] });
  const [loaded, setLoaded] = useState(false);
  const [categoria, setCategoria] = useState("");
  const [titolo, setTitolo] = useState("");
  const [direttive, setDirettive] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/vesta-libreria").then((r) => {
    setData(r.data);
    setCategoria((c) => c || r.data.categorie[0]?.nome || "");
  }).finally(() => setLoaded(true)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data.documenti.some((d) => d.stato === "in_lettura")) {
      const t = setTimeout(load, 4000);
      return () => clearTimeout(t);
    }
  }, [data, load]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!categoria) { toast.error("Scegli prima una categoria"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("categoria", categoria);
    fd.append("titolo", titolo);
    fd.append("direttive", direttive);
    try {
      await api.post("/vesta-libreria/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setTitolo(""); setDirettive("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Documento caricato: lettura in corso");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
    setBusy(false);
  };

  const addUrl = async () => {
    if (!url.trim()) return;
    if (!categoria) { toast.error("Scegli prima una categoria"); return; }
    setBusy(true);
    try {
      await api.post("/vesta-libreria/url", { url: url.trim(), categoria, titolo, direttive });
      setUrl(""); setTitolo(""); setDirettive("");
      toast.success("Link aggiunto: lettura in corso");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setBusy(false);
  };

  const del = async (d) => {
    if (!window.confirm(`Eliminare «${d.titolo}» dalla conoscenza di Vesta?`)) return;
    try { await api.delete(`/vesta-libreria/${d.id}`); load(); toast.success("Documento eliminato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const reindex = async (d) => {
    try { await api.post(`/vesta-libreria/${d.id}/reindex`); load(); toast.success("Rilettura avviata"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const byCat = data.categorie.map((c) => ({ cat: c, docs: data.documenti.filter((d) => d.categoria === c.nome) }));
  const orfani = data.documenti.filter((d) => !data.categorie.some((c) => c.nome === d.categoria));
  if (orfani.length) byCat.push({ cat: { id: "orfani", nome: "Senza categoria" }, docs: orfani });

  return (
    <div className="space-y-4" data-testid="libreria-documenti">
      <Card className="space-y-4 p-4">
        <div>
          <div className="mb-2 text-sm font-semibold">Categorie</div>
          <CategorieManager cats={data.categorie} reload={load} />
        </div>
        <div className="border-t pt-4">
          <div className="mb-2 text-sm font-semibold">Aggiungi un documento</div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56">
              <label className="mb-1 block text-xs text-muted-foreground">Categoria</label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger data-testid="lib-upload-categoria"><SelectValue placeholder="Scegli categoria" /></SelectTrigger>
                <SelectContent>
                  {data.categorie.map((c) => <SelectItem key={c.id} value={c.nome} data-testid={`lib-upload-cat-opt-${c.id}`}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[14rem] flex-1">
              <label className="mb-1 block text-xs text-muted-foreground">Titolo (facoltativo)</label>
              <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Es. Linee guida GSE 2026" data-testid="lib-upload-titolo" />
            </div>
          </div>
          <div className="mt-3">
            <label className="mb-1 flex items-center gap-1 text-xs text-muted-foreground"><Info className="h-3.5 w-3.5" />Direttive per Vesta (facoltative): come interpretare il documento, su cosa puntare, cosa dire e cosa non dire</label>
            <Textarea value={direttive} onChange={(e) => setDirettive(e.target.value)} rows={2} placeholder="Es. Studio sulla resa dei moduli Half-Cell. Usalo per spiegare i vantaggi in ombreggiamento parziale. Non dare percentuali di resa non presenti nel documento." data-testid="lib-upload-direttive" />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="lib-file-input" onChange={(e) => uploadFile(e.target.files?.[0])} />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="lib-upload-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Carica PDF / Word / testo
            </Button>
            <span className="text-xs text-muted-foreground">oppure</span>
            <div className="flex min-w-[18rem] flex-1 items-center gap-2">
              <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addUrl()} placeholder="https://… link a una pagina web" data-testid="lib-url-input" />
              <Button variant="outline" onClick={addUrl} disabled={busy} data-testid="lib-url-btn"><Link2 className="mr-1 h-4 w-4" />Aggiungi link</Button>
            </div>
          </div>
        </div>
      </Card>

      {loaded && !data.documenti.length && <p className="py-6 text-sm text-muted-foreground" data-testid="lib-empty">Nessun documento in libreria. Carica studi tecnici, incentivi, normative e link: Vesta li userà per rispondere.</p>}

      {byCat.filter((g) => g.docs.length).map((g) => (
        <div key={g.cat.id} className="space-y-2" data-testid={`lib-group-${g.cat.id}`}>
          <div className="text-sm font-semibold text-muted-foreground">{g.cat.nome} ({g.docs.length})</div>
          {g.docs.map((d) => {
            const Icon = TIPO_ICON[d.tipo] || FileText;
            return (
              <Card key={d.id} className="flex flex-col gap-2 p-3" data-testid={`lib-doc-${d.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{d.titolo}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="uppercase">{d.tipo}</span>
                        {d.original_filename && <span className="truncate">{d.original_filename}</span>}
                        {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="truncate text-blue-600 hover:underline">{d.url}</a>}
                        <StatoBadge d={d} />
                        {d.stato === "errore" && d.errore && <span className="text-destructive">{d.errore}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {d.stato === "errore" && <Button size="icon" variant="ghost" onClick={() => reindex(d)} data-testid={`lib-reindex-${d.id}`} title="Riprova lettura"><RefreshCw className="h-4 w-4" /></Button>}
                    <Button size="icon" variant="ghost" onClick={() => del(d)} data-testid={`lib-del-${d.id}`} title="Elimina"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
                <DirettiveEditor d={d} reload={load} />
              </Card>
            );
          })}
        </div>
      ))}
    </div>
  );
}
